import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { NgIf, NgClass } from '@angular/common';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { UserService } from '../../services/user.service';
import { OrganizationBasicInfo, ServiceResponse, UserDetails, UserLogin } from '../../models/user';
import { simpleTransitions } from '../../shared/animations/simple-transitions';

import { ValidateUserErrorDialogComponent } from './dialogs/validate-user-error.component';

const passwordPatternLowerCase = /[a-z]/;
const passwordPatternUpperCase = /[A-Z]/;
const passwordPatternDigit = /\d/;
const passwordPatternSpecialChars = /[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]/;
const passwordMinChars = 15;

function namedValidator(validator: ValidatorFn, name: string): ValidatorFn {
	return control => {
		const errors = validator(control);
		if (!errors) {
			return null;
		}
		return Object.fromEntries(Object.entries(errors).map(([key, value]) => [key + name, value]));
	};
}

const confirmPasswordValidator = (group: AbstractControl) => {
	const password = group.get('password');
	const confirmation = group.get('confirmPassword');

	if (!password || !confirmation) {
		return null;
	}

	if ((<string>confirmation.value).length > 0 && confirmation.value !== password.value) {
		confirmation.setErrors({ notMatch: true });
	}

	return null;
};

@Component({
	selector: 'copo-validate-user',
	templateUrl: './validate-user.component.html',
	animations: simpleTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrls: ['./validate-user.component.css'],
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatIconModule,
		TranslateModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyButtonModule,
		NgIf,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyFormFieldModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyInputModule,
		NgClass,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyTableModule,
		LoadingSpinnerComponent,
		RouterLink
	],
})
export class ValidateUserComponent implements OnDestroy {
	@HostBinding('class.mat-typography') matTypography = true;
	@HostBinding('class.full-height') fullHeight = true;

	sessionToken: string | undefined;
	userSub: Subscription | undefined;
	isLoading = true;
	userClone?: UserDetails;
	isInValidSessionToken = false;
	validateUserFormGroup!: FormGroup;
	passwordPatternLowerCase = namedValidator(Validators.pattern(passwordPatternLowerCase), 'LowerCase');
	passwordPatternUpperCase = namedValidator(Validators.pattern(passwordPatternUpperCase), 'UpperCase');
	passwordPatternDigit = namedValidator(Validators.pattern(passwordPatternDigit), 'Digit');
	passwordPatternSpecialChars = namedValidator(Validators.pattern(passwordPatternSpecialChars), 'SpecialChars');
	passwordMinChars = Validators.minLength(passwordMinChars);
	passwordPatternStr = `(?=.*${ passwordPatternLowerCase.source
	})(?=.*${ passwordPatternUpperCase.source
	})(?=.*${ passwordPatternDigit.source
	})(?=.*${ passwordPatternSpecialChars.source
	}).{${ passwordMinChars
	},}`;

	serviceList: ServiceResponse[] = [];
	displayedColumns: string[] = ['name', 'description'];
	orgInfo?: OrganizationBasicInfo;
	showError = false;
	isResetPassword = false;
	isPasswordExpired = false;
	tokenExpiredMessage = '';
	constructor(
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		// eslint-disable-next-line deprecation/deprecation
		public dialog: MatDialog,
		private readonly router: Router,
	) {
		const params = <Record<string, string | undefined>> this.route.snapshot.queryParams;
		this.isResetPassword = this.router.url.includes('reset-password');
		this.isPasswordExpired = this.router.url.includes('expired-password');
		this.sessionToken = params['session_token'];
		if (!this.sessionToken) {
			this.dialog.open(ValidateUserErrorDialogComponent, {
				width: '550px',
				panelClass: 'validate-user-dialog',
				data: { errorStatus: 'Invalid link' }
			});
			return;
		}

		this.userSub = this.userService.getUserByToken(this.sessionToken)
			.subscribe({
				next: (data: UserDetails) => {
					this.isLoading = false;
					this.serviceList = data.userServices;
					this.orgInfo = data.orgInfo;
					this.validateUserFormGroup.patchValue({
						firstName: data.firstName,
						lastName: data.lastName,
						email: data.email,
						username: data.userName && data.userName.length > 0 ? data.userName : ''
					});

					this.userClone = {
						...data,
					};
					this.isLoading = false;
				},
				error: (err: unknown) => {
					this.isLoading = false;
					console.error(err);
					this.isInValidSessionToken = true;
					this.tokenExpiredMessage = this.isResetPassword ? 'reset-password-link-expired'
						: 'validate-user-link-expired';
				}
			});
		this.validateUserFormGroup = new FormGroup(
			{
				firstName: new FormControl(
					{ value: '', disabled: this.isResetPassword || this.isPasswordExpired },
					Validators.required
				),
				lastName: new FormControl(
					{ value: '', disabled: this.isResetPassword || this.isPasswordExpired },
					Validators.required
				),
				email: new FormControl({ value: '', disabled: this.isResetPassword || this.isPasswordExpired }, [
					Validators.required,
					Validators.email,
					Validators.pattern('^[a-zA-Z0-9._\'-]+@[a-zA-Z0-9.-]{2,}[.][a-zA-Z]{2,}$')
				]),
				username: new FormControl(
					{ value: '', disabled: this.isResetPassword || this.isPasswordExpired },
					Validators.required
				),
				password: new FormControl('', [
					Validators.required,
					this.passwordPatternLowerCase,
					this.passwordPatternUpperCase,
					this.passwordPatternDigit,
					this.passwordPatternSpecialChars,
					this.passwordMinChars
				]),
				confirmPassword: new FormControl('', [Validators.required]),
				oldPassword: new FormControl('', { validators: this.isPasswordExpired ? Validators.required : null })
			},
			confirmPasswordValidator
		);
	}

	get confirmPassword() {
		return this.validateUserFormGroup.get('confirmPassword');
	}

	get password() {
		return this.validateUserFormGroup.get('password');
	}

	get oldPassword() {
		return this.validateUserFormGroup.get('oldPassword');
	}

	get firstName() {
		return this.validateUserFormGroup.get('firsName');
	}

	get lastName() {
		return this.validateUserFormGroup.get('lastName');
	}

	get email() {
		return this.validateUserFormGroup.get('email');
	}

	get username() {
		return this.validateUserFormGroup.get('username');
	}

	submit() {
		if (this.isResetPassword) {
			this.resetPassword();
		} else if (this.isPasswordExpired) {
			this.updateUserPassword();
		} else {
			this.createLogin();
		}
	}

	createLogin() {
		if (this.checkErrors()) {
			if (!this.sessionToken) {
				this.dialog.open(ValidateUserErrorDialogComponent, {
					width: '550px',
					panelClass: 'validate-user-dialog',
					data: { errorStatus: 'Invalid link' }
				});
				return;
			}
			const userLogin: UserLogin = {
				login: <string> this.validateUserFormGroup.get('username')?.value,
				password: <string> this.validateUserFormGroup.get('password')?.value,
				confirmPassword: <string> this.validateUserFormGroup.get('confirmPassword')?.value,
				firstName: this.userClone && <string> this.validateUserFormGroup.get('firstName')?.value
					!== this.userClone.firstName
					? <string> this.validateUserFormGroup.get('firstName')?.value : undefined,
				lastName: this.userClone && <string> this.validateUserFormGroup.get('lastName')?.value
					!== this.userClone.lastName
					? <string> this.validateUserFormGroup.get('lastName')?.value : undefined,
				email: this.userClone && <string> this.validateUserFormGroup.get('email')?.value
					!== this.userClone.email
					? <string> this.validateUserFormGroup.get('email')?.value : undefined,

			};
			this.userService.createLogin(userLogin, this.sessionToken).then(() => {
				location.replace(this.userService.accessUrl().toString());
			}).catch(error => {
				if (error instanceof HttpErrorResponse) {
					this.dialog.open(ValidateUserErrorDialogComponent, {
						width: '550px',
						panelClass: 'validate-user-dialog',
						data: { errorStatus: (<{ message: string }>error.error).message }
					});
				}
			});
		}
	}

	updateUserPassword() {
		if (!this.validateUserFormGroup.valid) {
			return;
		}
		const oldPassword = <string | undefined> this.validateUserFormGroup.get('oldPassword')?.value;
		const password = <string | undefined> this.validateUserFormGroup.get('password')?.value;
		const confirmPassword = <string | undefined> this.validateUserFormGroup.get('confirmPassword')?.value;
		if (!this.sessionToken || !password) {
			return;
		}
		if (oldPassword && password && confirmPassword) {
			this.userService.updateUserPassword(oldPassword, password, confirmPassword, this.sessionToken).then(() => {
				location.replace(this.userService.accessUrl().toString());
			}).catch(error => {
				if (error instanceof HttpErrorResponse) {
					this.dialog.open(ValidateUserErrorDialogComponent, {
						width: '550px',
						panelClass: 'validate-user-dialog',
						data: { errorStatus: (<{ message: string }>error.error).message }
					});
				}
			});
		}
	}

	resetPassword() {
		if (!this.validateUserFormGroup.valid) {
			return;
		}
		const password = <string | undefined> this.validateUserFormGroup.get('password')?.value;
		if (!this.sessionToken || !password) {
			return;
		}
		this.userService.confirmPassword(this.sessionToken, password).then(() => {
			location.replace(this.userService.accessUrl().toString());
		}).catch(error => {
			if (error instanceof HttpErrorResponse) {
				this.dialog.open(ValidateUserErrorDialogComponent, {
					width: '550px',
					panelClass: 'validate-user-dialog',
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					data: { errorStatus: (<{ message: string }>error.error.status) }
				});
			}
		});
	}

	notFoundUrl() {
		return new URL('**', location.origin);
	}

	checkErrors() {
		this.validateUserFormGroup.markAllAsTouched();
		this.showError = true;
		return this.validateUserFormGroup.valid;
	}

	ngOnDestroy() {
		if (this.userSub) {
			this.userSub.unsubscribe();
		}
	}

	match(type: string) {
		switch (type) {
		case 'charLength':
			return (<string> this.validateUserFormGroup.get('password')?.value).length >= 15;
		case 'lower':
			return passwordPatternLowerCase.test(<string> this.validateUserFormGroup.get('password')?.value);
		case 'upper':
			return passwordPatternUpperCase.test(<string> this.validateUserFormGroup.get('password')?.value);
		case 'number':
			return passwordPatternDigit.test(<string> this.validateUserFormGroup.get('password')?.value);
		case 'special':
			return passwordPatternSpecialChars.test(<string> this.validateUserFormGroup.get('password')?.value);
		default:
			return undefined;
		}
	}
}
